import tokens from '@pleo-io/telescope-tokens'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import Image from '@/components/image'
import {Tiers} from '@/components/pricing'
import {Text, text} from '@/components/text'
import useMediaQuery from '@/hooks/use-media-query'
import {usePricingCtas} from '@/hooks/use-pricing-ctas'
import {Chevron} from '@/images/icons/chevron'
import {BackgroundWrapper, Container, Section, container} from '@/styles/grid'
import {headerSizes, zIndex} from '@/theme'
import {TiersMap} from '@/types/pricing'
import getEnvironmentFormConfig from '@/utils/get-environment-field'
import mq from '@/utils/media-query'

import type {TierTypes} from '@/components/pricing'
import type {PricingPageQuery} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'
import type {ImageSource} from '@/types/image'
import type {PageProps} from 'gatsby'

import {PricingComparisonTable} from './comparison-table'
import {PlanSelectionOverlay} from './plan-selection-overlay'

type PricingPageProps = PageProps<PricingPageQuery>['data']['prismicPricingPage']['data']
type PricingTiersProps = PageProps<PricingPageQuery>['data']['prismicPricingTiers']['data']

interface PricingComparisonProps {
    content: {
        title: string
        titleImage: ImageSource
        tablesFinePrint?: string
        pricingTiersCtasLabels: string[]
        layoverLabels: PricingPageProps['comparison_layover'][number]
        tables: PricingPageProps['comparison_tables']
        form_production: PricingTiersProps['form_production']
        form_staging: PricingTiersProps['form_staging']
        signup_modal_free: PricingTiersProps['signup_modal_free']
        signup_modal_pro: PricingTiersProps['signup_modal_pro']
    }
}

export const PricingComparison = ({content}: PricingComparisonProps) => {
    const isDesktop = useMediaQuery.min('md')
    const [comparedTiers, setComparedTiers] = React.useState<[TierTypes, TierTypes]>([
        'starter',
        'essential',
    ])
    const [showLayover, setShowLayover] = React.useState(false)
    const [selectedPlanDropdownValue, setSelectedPlanDropdownValue] = React.useState(
        comparedTiers[0],
    )
    const [updatingTableSide, setUpdatingTableSide] = React.useState<'left' | 'right' | undefined>()
    const planSelectorRef = React.useRef<HTMLDivElement>(null)

    const formConfig = getEnvironmentFormConfig({
        production: content.form_production,
        staging: content.form_staging,
    })

    React.useEffect(() => {
        if (updatingTableSide) {
            const newComparedTiers: [TierTypes, TierTypes] =
                updatingTableSide === 'left'
                    ? [selectedPlanDropdownValue, comparedTiers[1]]
                    : [comparedTiers[0], selectedPlanDropdownValue]

            setComparedTiers(newComparedTiers)
            closeLayover()
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPlanDropdownValue])

    React.useEffect(() => {
        closeLayover()
    }, [isDesktop])

    React.useEffect(() => {
        // locks scroll when layover is open
        document.body.style.overflow = showLayover ? 'hidden' : ''
    }, [showLayover])

    const handleClick = usePricingCtas({
        formConfig: formConfig as FormConfig,
        signupModalFree: content.signup_modal_free,
        signupModalPro: content.signup_modal_pro,
    })

    const scrollPlanSelectorToTop = () => {
        if (planSelectorRef.current) {
            const elementPosition =
                planSelectorRef.current.getBoundingClientRect().top + window.pageYOffset
            const offsetPosition = elementPosition - headerSizes.heightSmall

            window.scrollTo({
                top: offsetPosition,
                behavior: 'auto',
            })
        }
    }

    const closeLayover = () => {
        setUpdatingTableSide(undefined)
        setShowLayover(false)
    }

    return (
        <BackgroundWrapper $color="muted">
            <Container>
                <Text $variant="heading-large" as="h2" style={{maxWidth: 540}}>
                    {content.title}
                </Text>
            </Container>

            <StyledSection edgeToEdge style={{paddingTop: 0}}>
                <TablesWrapper>
                    {content.titleImage && (
                        <ImageWrapper>
                            <StyledImage image={content.titleImage} />
                        </ImageWrapper>
                    )}

                    <PlanSelectors $sticky={!showLayover} ref={planSelectorRef}>
                        {['left', 'right'].map((side, index) => (
                            <PlanSelector
                                key={side}
                                onClick={() => {
                                    if (showLayover) {
                                        closeLayover()
                                    } else {
                                        setUpdatingTableSide(side as 'left' | 'right')
                                        scrollPlanSelectorToTop()
                                        setShowLayover(true)
                                    }
                                }}
                            >
                                {(!updatingTableSide || updatingTableSide === side) && (
                                    <>
                                        <Text $variant="paragraph-large-medium">
                                            {TiersMap[comparedTiers[index]]}
                                        </Text>
                                        <Arrow
                                            $isExpanded={showLayover && updatingTableSide === side}
                                        />
                                    </>
                                )}
                            </PlanSelector>
                        ))}

                        {Object.values(TiersMap).map((tier) => (
                            <TableHeaderText key={tier} $variant="paragraph-large-medium" $center>
                                {tier}
                            </TableHeaderText>
                        ))}
                    </PlanSelectors>

                    {showLayover && (
                        <PlanSelectionOverlay
                            selectedValue={selectedPlanDropdownValue}
                            onChange={setSelectedPlanDropdownValue}
                            content={{
                                title: content.layoverLabels.heading,
                                options: (Object.keys(TiersMap) as (keyof typeof TiersMap)[]).map(
                                    (key) => ({
                                        value: key,
                                        label: TiersMap[key],
                                        description:
                                            content.layoverLabels[
                                                `${key}_description` as keyof typeof content.layoverLabels
                                            ],
                                    }),
                                ),
                            }}
                        />
                    )}

                    {content.tables.map(({comparison_table}) => (
                        <PricingComparisonTable
                            key={comparison_table.document.data.title}
                            comparedTiers={
                                isDesktop
                                    ? ['starter', 'essential', 'advanced', 'beyond']
                                    : comparedTiers
                            }
                            content={comparison_table.document.data}
                        />
                    ))}
                    <CTASWrapper>
                        {[isDesktop ? Tiers : comparedTiers]
                            .flat()
                            .map((tier: TierTypes, index) => (
                                <CTAWrapper key={tier}>
                                    <PricingPlanCta
                                        $variant="default"
                                        onClick={() =>
                                            handleClick(isDesktop ? tier : comparedTiers[index])
                                        }
                                    >
                                        {content.pricingTiersCtasLabels[index]}
                                    </PricingPlanCta>
                                </CTAWrapper>
                            ))}
                    </CTASWrapper>
                </TablesWrapper>

                {content.tablesFinePrint && (
                    <SubText $variant="label-base">{content.tablesFinePrint}</SubText>
                )}
            </StyledSection>
        </BackgroundWrapper>
    )
}

const StyledSection = styled(Section)`
    ${mq.min('sm')} {
        ${container}
    }
`

const ImageWrapper = styled.div`
    ${mq.min('sm')} {
        padding-left: 40%;
    }

    ${mq.min('md')} {
        padding-left: 30%;
    }
`

const CTASWrapper = styled.div`
    display: flex;
    background-color: ${({theme}) => theme.colors.background.default};

    ${mq.min('sm')} {
        padding-left: 40%;
    }

    ${mq.min('md')} {
        padding-left: 30%;
    }
`

const PricingPlanCta = styled(Button)`
    ${text({$variant: 'label-base', $color: 'inverted'})}
    height: 32px;

    ${mq.min('md')} {
        width: fit-content;
        padding: ${({theme}) => `${theme.spacings.s14} ${theme.spacings.s12}`};
    }

    ${mq.min('lg')} {
        padding: ${({theme}) => `${theme.spacings.s14} ${theme.spacings.s28}`};
    }
`

const CTAWrapper = styled.div`
    flex-basis: 50%;
    display: flex;
    justify-content: center;
    padding: ${({theme}) => `${theme.spacings.s20} ${theme.spacings.s10}`};

    ${mq.min('sm')} {
        padding: ${({theme}) => `${theme.spacings.s20} ${theme.spacings.s10}`};

        &:first-child {
            border-left: ${({theme}) => theme.borders.default};
        }
    }

    ${mq.min('lg')} {
        padding: ${({theme}) => `${theme.spacings.s20} 0`};
    }
`

const TableHeaderText = styled(Text)`
    display: none;

    ${mq.min('md')} {
        flex-basis: 100%;
        display: block;
        border-left: ${({theme}) => theme.borders.default};
        padding: ${({theme}) => `${theme.spacings.s20}`};
    }
`

const SubText = styled(Text)`
    margin-left: ${({theme}) => theme.spacings.s24};

    ${mq.min('sm')} {
        margin-left: 0;
        margin-top: ${({theme}) => theme.spacings.s40};
    }
`

const PlanSelectors = styled.div<{$sticky: boolean}>`
    background-color: ${({theme}) => theme.colors.background.default};
    border-top: ${({theme}) => theme.borders.default};
    border-bottom: ${({theme}) => theme.borders.default};
    display: flex;
    position: ${(props) => (props.$sticky ? 'sticky' : 'fixed')};
    top: calc(${headerSizes.heightSmall}px);
    z-index: ${zIndex.header - 1};
    width: 100%;

    ${mq.min('sm')} {
        padding-left: ${(props) => (props.$sticky ? '40%' : 'calc(40% - 19px)')};
        width: ${(props) => (props.$sticky ? '100%' : 'calc(100% - 48px)')};
    }

    ${mq.min('md')} {
        padding-left: 30%;
    }

    ${mq.min('lg')} {
        top: calc(${headerSizes.heightLarge}px);
    }
`

const PlanSelector = styled.button`
    all: unset;
    width: 50%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${({theme}) => `${theme.spacings.s20} ${theme.spacings.s24}`};

    &:hover {
        cursor: pointer;
    }

    &:last-of-type {
        border-left: ${({theme}) => theme.borders.default};
    }

    ${mq.min('sm')} {
        border-left: ${({theme}) => theme.borders.default};
    }

    ${mq.min('md')} {
        display: none;
    }
`

const StyledImage = styled(Image)`
    margin: ${({theme}) => theme.spacings.s40} auto ${({theme}) => theme.spacings.s10};

    ${mq.min('sm')} {
        margin: ${({theme}) => theme.spacings.s40} auto ${({theme}) => theme.spacings.s10};
    }
`

const TablesWrapper = styled.div`
    margin-bottom: ${({theme}) => theme.spacings.s20};
    position: relative;
`

const Arrow = styled(Chevron)<{$isExpanded: boolean}>`
    width: ${({theme}) => `${theme.spacings.s12}`};
    height: ${({theme}) => `${theme.spacings.s12}`};
    transition: transform ${tokens.smoothInOut};
    transform: ${(props) => props.$isExpanded && 'rotate(-180deg)'};
`
